import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import { Button } from '../components/ui/button';
import {
  Globe, BarChart3, Layers, MapPin, AlertTriangle, TrendingUp,
  ChevronRight, Loader2, Plus, Trash2, ArrowRight, Shield,
  Clock, Check, X, Zap, Factory, Ship, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDelete } from '../lib/apiClient';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'exposure', label: 'Exposure Map', icon: Globe },
  { id: 'scenarios', label: 'Scenario Modeling', icon: Layers },
  { id: 'markets', label: 'Market Intelligence', icon: MapPin },
];

const getRiskColor = (level) => {
  if (!level) return 'text-slate-500 bg-slate-50';
  const l = level.toLowerCase();
  if (l === 'low') return 'text-emerald-700 bg-emerald-50';
  if (l.includes('low')) return 'text-teal-700 bg-teal-50';
  if (l === 'medium') return 'text-amber-700 bg-amber-50';
  if (l === 'high') return 'text-red-700 bg-red-50';
  return 'text-slate-500 bg-slate-50';
};

const fmt = (n) => {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const SupplyChainIntelligence = ({ user, setUser }) => {
  const sidebarCollapsed = useSidebarState();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [exposure, setExposure] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);

  // Scenario builder
  const [showBuilder, setShowBuilder] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioDesc, setScenarioDesc] = useState('');
  const [shifts, setShifts] = useState([{ from_country: '', to_country: '', hs_chapter: '', percentage: 50 }]);
  const [creating, setCreating] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await apiGet('/api/supply-chain/dashboard');
      setDashboard(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchMarkets = useCallback(async () => {
    try {
      const d = await apiGet('/api/supply-chain/markets');
      setMarkets(d.markets || []);
    } catch { /* ignore */ }
  }, []);

  const fetchExposure = useCallback(async () => {
    try {
      const data = await apiGet('/api/supply-chain/exposure');
      setExposure(data);
    } catch { /* ignore */ }
  }, []);

  const fetchScenarios = useCallback(async () => {
    try {
      const d = await apiGet('/api/supply-chain/scenarios');
      setScenarios(d.scenarios || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchMarkets();
  }, [fetchDashboard, fetchMarkets]);

  useEffect(() => {
    if (activeTab === 'exposure' && !exposure) fetchExposure();
    if (activeTab === 'scenarios' && scenarios.length === 0) fetchScenarios();
  }, [activeTab, exposure, fetchExposure, fetchScenarios, scenarios.length]);

  const createScenario = async () => {
    if (!scenarioName.trim()) { toast.error('Name your scenario'); return; }
    if (shifts.some(s => !s.from_country || !s.to_country)) { toast.error('Fill in all country fields'); return; }
    setCreating(true);
    try {
      const scenario = await apiPost('/api/supply-chain/scenarios', {
        name: scenarioName, description: scenarioDesc, shifts,
      });
      setScenarios(prev => [scenario, ...prev]);
      setSelectedScenario(scenario);
      setShowBuilder(false);
      setScenarioName(''); setScenarioDesc('');
      setShifts([{ from_country: '', to_country: '', hs_chapter: '', percentage: 50 }]);
      toast.success('Scenario created');
    } catch (err) {
      toast.error(err?.message || 'Failed to create scenario');
    }
    finally { setCreating(false); }
  };

  const deleteScenario = async (id) => {
    try {
      await apiDelete(`/api/supply-chain/scenarios/${id}`);
      setScenarios(prev => prev.filter(s => s.scenario_id !== id));
      if (selectedScenario?.scenario_id === id) setSelectedScenario(null);
      toast.success('Scenario deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const addShift = () => setShifts(prev => [...prev, { from_country: '', to_country: '', hs_chapter: '', percentage: 50 }]);
  const removeShift = (i) => setShifts(prev => prev.filter((_, idx) => idx !== i));
  const updateShift = (i, field, val) => setShifts(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  // Available country codes for dropdowns
  const countryCodes = markets.map(m => ({ code: m.code, name: m.name }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex">
        <Sidebar user={user} setUser={setUser} activePage="supply-chain-intel" />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-[#0B3D91] animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="supply-chain-intel" />

      <main className="flex-1 p-6 lg:p-8 lg:pt-24 transition-all duration-300">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <h1 className="font-outfit text-3xl font-bold text-[#0B3D91]" data-testid="sc-intel-title">
              Supply Chain Intelligence
            </h1>
            <p className="text-slate-500 text-sm mt-1">Portfolio-level sourcing strategy and duty exposure analysis</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200 shadow-sm mb-6" data-testid="sc-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                    isActive ? 'bg-[#0B3D91] text-white shadow-md' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`sc-tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && dashboard && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {!dashboard.exposure.data_available ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-slate-200 shadow-sm" data-testid="sc-empty-state">
                  <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h2 className="font-outfit text-xl font-bold text-slate-700 mb-2">No Exposure Data Yet</h2>
                  <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">
                    Run a Financial Impact Analysis first. Supply Chain Intelligence aggregates your existing data to build exposure maps, scenario models, and market insights.
                  </p>
                  <Button className="bg-[#0B3D91] text-white" onClick={() => window.location.href = '/analyzer?tab=financial'} data-testid="sc-go-financial">
                    Run Financial Analysis <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Total Duty Exposure', value: fmt(dashboard.exposure.total_duty), icon: AlertTriangle, color: 'from-red-50 to-orange-50', textColor: 'text-red-700' },
                      { label: 'Portfolio Value', value: fmt(dashboard.exposure.total_value), icon: TrendingUp, color: 'from-blue-50 to-indigo-50', textColor: 'text-blue-700' },
                      { label: 'Source Countries', value: dashboard.exposure.unique_countries, icon: Globe, color: 'from-emerald-50 to-teal-50', textColor: 'text-emerald-700' },
                      { label: 'Section 301 SKUs', value: dashboard.exposure.section_301_skus, icon: Shield, color: 'from-amber-50 to-yellow-50', textColor: 'text-amber-700' },
                    ].map((kpi, i) => {
                      const Icon = kpi.icon;
                      return (
                        <div key={i} className={`bg-gradient-to-br ${kpi.color} rounded-2xl p-5 border border-slate-100`} data-testid={`sc-kpi-${i}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${kpi.textColor}`} />
                            <span className="text-xs font-medium text-slate-500">{kpi.label}</span>
                          </div>
                          <p className={`font-outfit text-2xl font-bold ${kpi.textColor}`}>{kpi.value}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Quick Actions */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <button onClick={() => setActiveTab('exposure')} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all text-left group" data-testid="sc-action-exposure">
                      <Globe className="w-8 h-8 text-[#0B3D91] mb-3" />
                      <h3 className="font-outfit font-semibold text-slate-900 mb-1">Exposure Map</h3>
                      <p className="text-xs text-slate-500">See where your duty concentration is highest</p>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 group-hover:text-[#0B3D91] transition-colors" />
                    </button>
                    <button onClick={() => { setActiveTab('scenarios'); setShowBuilder(true); }} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all text-left group" data-testid="sc-action-scenario">
                      <Layers className="w-8 h-8 text-indigo-600 mb-3" />
                      <h3 className="font-outfit font-semibold text-slate-900 mb-1">Model a Scenario</h3>
                      <p className="text-xs text-slate-500">What if you shifted sourcing to another country?</p>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 group-hover:text-indigo-600 transition-colors" />
                    </button>
                    <button onClick={() => setActiveTab('markets')} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all text-left group" data-testid="sc-action-markets">
                      <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
                      <h3 className="font-outfit font-semibold text-slate-900 mb-1">Market Intelligence</h3>
                      <p className="text-xs text-slate-500">Explore 12 country profiles with risk & tariff data</p>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 group-hover:text-emerald-600 transition-colors" />
                    </button>
                  </div>

                  {/* Scenario Summary */}
                  {dashboard.scenarios.count > 0 && (
                    <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                      <h3 className="font-outfit font-semibold text-slate-900 mb-2">Best Scenario</h3>
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">{dashboard.scenarios.best_name}</span> — saves {fmt(dashboard.scenarios.best_savings)} in duty
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ===== EXPOSURE MAP TAB ===== */}
          {activeTab === 'exposure' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {!exposure ? (
                <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0B3D91] animate-spin" /></div>
              ) : exposure.summary.total_skus === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-slate-200" data-testid="exposure-empty">
                  <Globe className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-outfit text-lg font-bold text-slate-700 mb-1">No exposure data</h3>
                  <p className="text-sm text-slate-500">Run a Financial Impact Analysis to populate your exposure map.</p>
                </div>
              ) : (
                <>
                  {/* Summary Banner */}
                  <div className="bg-gradient-to-r from-[#0B3D91] to-indigo-700 rounded-2xl p-6 text-white" data-testid="exposure-summary">
                    <div className="grid sm:grid-cols-4 gap-6">
                      <div>
                        <p className="text-blue-200 text-xs mb-1">Total Duty Exposure</p>
                        <p className="font-outfit text-2xl font-bold">{fmt(exposure.summary.total_duty)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200 text-xs mb-1">Total Customs Value</p>
                        <p className="font-outfit text-2xl font-bold">{fmt(exposure.summary.total_value)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200 text-xs mb-1">Top Source</p>
                        <p className="font-outfit text-2xl font-bold">{exposure.summary.top_country || 'N/A'}</p>
                        <p className="text-blue-200 text-xs">{exposure.summary.top_country_concentration}% of duty</p>
                      </div>
                      <div>
                        <p className="text-blue-200 text-xs mb-1">SKUs Analyzed</p>
                        <p className="font-outfit text-2xl font-bold">{exposure.summary.total_skus.toLocaleString()}</p>
                        <p className="text-blue-200 text-xs">{exposure.summary.unique_countries} countries</p>
                      </div>
                    </div>
                  </div>

                  {/* By Country */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="exposure-by-country">
                    <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4">Duty Exposure by Country</h3>
                    <div className="space-y-3">
                      {exposure.by_country.map((c) => {
                        const pct = exposure.summary.total_duty > 0 ? (c.duty / exposure.summary.total_duty * 100) : 0;
                        return (
                          <div key={c.country} className="flex items-center gap-4" data-testid={`exposure-country-${c.country}`}>
                            <span className="text-sm font-medium text-slate-700 w-32 truncate">{c.country}</span>
                            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                              <div className="h-full bg-gradient-to-r from-[#0B3D91] to-indigo-500 rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                              <span className="absolute inset-0 flex items-center justify-end pr-3 text-xs font-medium text-slate-600">
                                {fmt(c.duty)}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500 w-12 text-right">{pct.toFixed(1)}%</span>
                            {c.section_301_count > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-medium">301</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section 301 */}
                  {exposure.section_301.exposed > 0 && (
                    <div className="bg-red-50 rounded-2xl p-6 border border-red-100" data-testid="exposure-301">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h3 className="font-outfit text-lg font-semibold text-red-800">Section 301 Exposure</h3>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-red-600">Exposed SKUs</p>
                          <p className="font-outfit text-xl font-bold text-red-800">{exposure.section_301.exposed}</p>
                        </div>
                        <div>
                          <p className="text-xs text-red-600">Exposed Value</p>
                          <p className="font-outfit text-xl font-bold text-red-800">{fmt(exposure.section_301.exposed_value)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-red-600">Not Exposed</p>
                          <p className="font-outfit text-xl font-bold text-emerald-700">{exposure.section_301.not_exposed}</p>
                        </div>
                      </div>
                      {exposure.section_301.top_exposed_skus.length > 0 && (
                        <div className="bg-white rounded-xl p-4">
                          <p className="text-xs font-semibold text-red-700 mb-2">Top Exposed SKUs</p>
                          <div className="space-y-2">
                            {exposure.section_301.top_exposed_skus.slice(0, 5).map((s, i) => (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 truncate flex-1 mr-4">{s.description}</span>
                                <span className="text-slate-500 text-xs mr-4">{s.country}</span>
                                <span className="font-medium text-red-700">{fmt(s.duty)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* By HS Chapter */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="exposure-by-chapter">
                    <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4">Duty by HS Chapter</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {exposure.by_hs_chapter.slice(0, 9).map((ch, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                          <div className="w-10 h-10 rounded-lg bg-[#0B3D91]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[#0B3D91]">{ch.chapter}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900">{fmt(ch.duty)}</p>
                            <p className="text-xs text-slate-500">{ch.skus} SKUs</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== SCENARIO MODELING TAB ===== */}
          {activeTab === 'scenarios' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Builder */}
              {showBuilder && (
                <div className="bg-white rounded-2xl p-6 border border-indigo-200 shadow-sm" data-testid="scenario-builder">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">New Scenario</h3>
                    <button onClick={() => setShowBuilder(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Scenario Name</label>
                      <input value={scenarioName} onChange={e => setScenarioName(e.target.value)} placeholder="e.g. Shift China → Vietnam" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B3D91]/20 focus:border-[#0B3D91] outline-none" data-testid="scenario-name-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Description (optional)</label>
                      <input value={scenarioDesc} onChange={e => setScenarioDesc(e.target.value)} placeholder="Brief description..." className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B3D91]/20 focus:border-[#0B3D91] outline-none" data-testid="scenario-desc-input" />
                    </div>
                  </div>

                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Sourcing Shifts</p>
                  {shifts.map((shift, i) => (
                    <div key={i} className="flex items-center gap-3 mb-3 p-3 bg-slate-50 rounded-xl" data-testid={`scenario-shift-${i}`}>
                      <div className="flex-1">
                        <label className="text-xs text-slate-500">From</label>
                        <select value={shift.from_country} onChange={e => updateShift(i, 'from_country', e.target.value)} className="w-full border rounded-lg px-2 py-1.5 text-sm mt-0.5" data-testid={`shift-from-${i}`}>
                          <option value="">Select country...</option>
                          {countryCodes.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                          <option value="China">China (text)</option>
                        </select>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 mt-4 flex-shrink-0" />
                      <div className="flex-1">
                        <label className="text-xs text-slate-500">To</label>
                        <select value={shift.to_country} onChange={e => updateShift(i, 'to_country', e.target.value)} className="w-full border rounded-lg px-2 py-1.5 text-sm mt-0.5" data-testid={`shift-to-${i}`}>
                          <option value="">Select country...</option>
                          {countryCodes.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="w-20">
                        <label className="text-xs text-slate-500">HS Ch.</label>
                        <input value={shift.hs_chapter} onChange={e => updateShift(i, 'hs_chapter', e.target.value)} placeholder="e.g. 85" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-0.5" data-testid={`shift-hs-${i}`} />
                      </div>
                      <div className="w-20">
                        <label className="text-xs text-slate-500">% shift</label>
                        <input type="number" value={shift.percentage} onChange={e => updateShift(i, 'percentage', Number(e.target.value))} min={1} max={100} className="w-full border rounded-lg px-2 py-1.5 text-sm mt-0.5" data-testid={`shift-pct-${i}`} />
                      </div>
                      {shifts.length > 1 && (
                        <button onClick={() => removeShift(i)} className="text-red-400 hover:text-red-600 mt-4"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center gap-3 mt-2">
                    <Button variant="outline" size="sm" onClick={addShift} data-testid="add-shift-btn"><Plus className="w-3 h-3 mr-1" /> Add Shift</Button>
                    <div className="flex-1" />
                    <Button variant="outline" size="sm" onClick={() => setShowBuilder(false)}>Cancel</Button>
                    <Button size="sm" className="bg-[#0B3D91] text-white" onClick={createScenario} disabled={creating} data-testid="create-scenario-btn">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run Scenario'}
                    </Button>
                  </div>
                </div>
              )}

              {!showBuilder && (
                <div className="flex justify-between items-center">
                  <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">Your Scenarios</h3>
                  <Button size="sm" className="bg-[#0B3D91] text-white" onClick={() => setShowBuilder(true)} data-testid="new-scenario-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Scenario
                  </Button>
                </div>
              )}

              {/* Scenario list */}
              {scenarios.length === 0 && !showBuilder ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-slate-200" data-testid="scenarios-empty">
                  <Layers className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-outfit text-lg font-bold text-slate-700 mb-1">No Scenarios Yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Model what-if sourcing shifts to see the duty impact.</p>
                  <Button className="bg-[#0B3D91] text-white" onClick={() => setShowBuilder(true)} data-testid="scenarios-empty-create">
                    <Plus className="w-4 h-4 mr-1" /> Create First Scenario
                  </Button>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Scenario Cards */}
                  <div className="space-y-3">
                    {scenarios.map((sc, i) => (
                      <div key={sc.scenario_id} className={`bg-white rounded-xl p-4 border cursor-pointer transition-all hover:shadow-md ${selectedScenario?.scenario_id === sc.scenario_id ? 'border-[#0B3D91] shadow-md' : 'border-slate-200'}`} onClick={() => setSelectedScenario(sc)} data-testid={`scenario-card-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900 text-sm">{sc.name}</h4>
                          <button onClick={(e) => { e.stopPropagation(); deleteScenario(sc.scenario_id); }} data-testid={`delete-scenario-${i}`} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        {sc.description && <p className="text-xs text-slate-500 mb-2">{sc.description}</p>}
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-slate-500">{sc.results?.length || 0} shift{sc.results?.length !== 1 ? 's' : ''}</span>
                          <span className={`font-bold ${sc.summary?.total_savings > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {sc.summary?.total_savings > 0 ? '+' : ''}{fmt(sc.summary?.total_savings)} savings
                          </span>
                          <span className="text-slate-400">{new Date(sc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Scenario Detail */}
                  {selectedScenario && (
                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="scenario-detail">
                      <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-1">{selectedScenario.name}</h3>
                      {selectedScenario.description && <p className="text-sm text-slate-500 mb-4">{selectedScenario.description}</p>}

                      {/* Summary */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 bg-slate-50 rounded-xl text-center">
                          <p className="text-xs text-slate-500">Current Duty</p>
                          <p className="font-bold text-slate-900">{fmt(selectedScenario.summary?.total_current_duty)}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-xl text-center">
                          <p className="text-xs text-slate-500">Projected</p>
                          <p className="font-bold text-blue-700">{fmt(selectedScenario.summary?.total_projected_duty)}</p>
                        </div>
                        <div className={`p-3 rounded-xl text-center ${selectedScenario.summary?.total_savings > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <p className="text-xs text-slate-500">Savings</p>
                          <p className={`font-bold ${selectedScenario.summary?.total_savings > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            {fmt(selectedScenario.summary?.total_savings)} ({selectedScenario.summary?.savings_pct}%)
                          </p>
                        </div>
                      </div>

                      {/* Shift Results */}
                      <div className="space-y-3">
                        {selectedScenario.results?.map((r, i) => (
                          <div key={i} className="p-4 bg-slate-50 rounded-xl" data-testid={`scenario-result-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-medium text-slate-700">{r.from_country}</span>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium text-[#0B3D91]">{r.to_country_name}</span>
                              {r.hs_chapter && <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">Ch. {r.hs_chapter}</span>}
                              <span className="text-xs text-slate-400 ml-auto">{r.percentage}% shifted</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-xs">
                              <div><span className="text-slate-500 block">SKUs</span><span className="font-bold text-slate-900">{r.skus_affected}</span></div>
                              <div><span className="text-slate-500 block">Savings</span><span className={`font-bold ${r.savings > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(r.savings)}</span></div>
                              <div><span className="text-slate-500 block">Lead Time</span><span className="font-bold text-slate-900">{r.lead_time_change}</span></div>
                              <div><span className="text-slate-500 block">FTA</span><span className="font-bold text-slate-900">{r.fta_benefit}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== MARKET INTELLIGENCE TAB ===== */}
          {activeTab === 'markets' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Market List */}
                <div className="lg:col-span-1 space-y-2" data-testid="market-list">
                  <h3 className="font-outfit font-semibold text-slate-700 text-sm mb-2">Country Profiles ({markets.length})</h3>
                  {markets.map((m, i) => (
                    <button
                      key={m.code}
                      onClick={() => setSelectedMarket(m)}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        selectedMarket?.code === m.code ? 'bg-[#0B3D91] text-white shadow-md' : 'bg-white border border-slate-200 hover:border-blue-300'
                      }`}
                      data-testid={`market-btn-${m.code}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${selectedMarket?.code === m.code ? 'text-white' : 'text-slate-900'}`}>{m.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          selectedMarket?.code === m.code ? 'bg-white/20 text-white' : getRiskColor(m.risk_level)
                        }`}>{m.risk_level}</span>
                      </div>
                      <div className={`flex items-center gap-3 mt-1 text-xs ${selectedMarket?.code === m.code ? 'text-blue-200' : 'text-slate-500'}`}>
                        <span>{m.region}</span>
                        <span>{m.base_tariff_avg}% avg tariff</span>
                        {m.section_301 && <span className="text-red-500 font-medium">301</span>}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Market Detail */}
                <div className="lg:col-span-2">
                  {selectedMarket ? (
                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="market-detail">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-outfit text-xl font-bold text-[#0B3D91]">{selectedMarket.name}</h3>
                          <p className="text-sm text-slate-500">{selectedMarket.region}</p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${getRiskColor(selectedMarket.risk_level)}`}>
                          Risk: {selectedMarket.risk_score}/100
                        </div>
                      </div>

                      {/* Key Metrics */}
                      <div className="grid sm:grid-cols-4 gap-3 mb-6">
                        <div className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-xs text-slate-500 mb-0.5">Base Tariff Avg</p>
                          <p className="font-bold text-slate-900">{selectedMarket.base_tariff_avg}%</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-xs text-slate-500 mb-0.5">Section 301</p>
                          <p className={`font-bold ${selectedMarket.section_301 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {selectedMarket.section_301 ? `Yes (+${selectedMarket.section_301_rate}%)` : 'No'}
                          </p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-xs text-slate-500 mb-0.5">Lead Time</p>
                          <p className="font-bold text-slate-900">{selectedMarket.lead_time_days} days</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-xs text-slate-500 mb-0.5">Quality</p>
                          <p className="font-bold text-slate-900">{selectedMarket.quality_tier}</p>
                        </div>
                      </div>

                      {/* FTAs */}
                      {selectedMarket.fta_status?.length > 0 && (
                        <div className="mb-4 p-3 bg-emerald-50 rounded-xl">
                          <p className="text-xs font-semibold text-emerald-700 mb-1">Active Trade Agreements</p>
                          <div className="flex gap-2">
                            {selectedMarket.fta_status.map((fta, i) => (
                              <span key={i} className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">{fta}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Strengths & Risks */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Strengths</p>
                          <ul className="space-y-1.5">
                            {selectedMarket.strengths?.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Risks</p>
                          <ul className="space-y-1.5">
                            {selectedMarket.risks?.map((r, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />{r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* GSP */}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-4 text-sm text-slate-600">
                        <span>GSP Eligible: <strong>{selectedMarket.gsp_eligible ? 'Yes' : 'No'}</strong></span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl p-10 text-center border border-slate-200" data-testid="market-empty-select">
                      <MapPin className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                      <h3 className="font-outfit text-lg font-bold text-slate-700 mb-1">Select a Country</h3>
                      <p className="text-sm text-slate-500">Click a country from the list to view its trade profile.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SupplyChainIntelligence;
